import type React from 'react';
import { useId } from 'react';

import { Separator, Text, ThemeProvider } from '@change/ds-core-ui';
import { Slider } from '@change/ds-forms';

import { Translate, TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { useEarningsCalculator } from './hooks/useEarningsCalculator';

// Ambassador payouts are always issued in USD, regardless of the viewer's locale/currency.
const EARNINGS_ESTIMATE_CURRENCY = 'USD';

// `narrowSymbol` keeps the bare "$". The default `symbol` qualifies USD outside the US, so the
// same amount renders as "US$400" (en-GB), "USD 400" (en-AU) or "400 $US" (fr-FR).
const EARNINGS_ESTIMATE_CURRENCY_OPTIONS = { currencyDisplay: 'narrowSymbol' } as const;

/**
 * Renders the slider's min/max end labels, taken from its own bounds rather than hardcoded, so
 * they can't drift from the real range and read correctly in locales that group digits
 * differently. Deferred to ClientRender for the same reason as the readout below: localized
 * output must not differ between the server render and hydration. Extracted only to keep the
 * section under max-lines-per-function.
 */
const SliderRangeLabels: React.FC<{ min: number; max: number }> = ({ min, max }) => {
	const { translate, localizeNumber } = useI18n();
	const maxLabel = (value: string) => translate('corgi.ambassadors.earnings.slider_max_label', { value });

	return (
		<div className="mt-2 flex justify-between">
			<Text variant="body" size="x-small" className="text-typography-weak">
				<ClientRender placeholder={String(min)}>{() => localizeNumber(min)}</ClientRender>
			</Text>
			<Text variant="body" size="x-small" className="text-typography-weak">
				<ClientRender placeholder={maxLabel(String(max))}>{() => maxLabel(localizeNumber(max))}</ClientRender>
			</Text>
		</div>
	);
};

const EarningsLeadIn: React.FC = () => (
	<div className="mb-8 flex flex-col gap-2.5">
		<Text as="p" variant="body" size="large" className="text-typography-strong">
			<TranslateHtml value="corgi.ambassadors.earnings.lead_signature" />
		</Text>
		<Text as="p" variant="body" size="medium" className="text-typography-strong flex items-baseline gap-2">
			<Text aria-hidden="true" as="span" size="medium" fontWeight="bold" className="text-stroke-brand-strong">
				+
			</Text>
			<Text as="span" variant="body" size="medium">
				<Translate value="corgi.ambassadors.earnings.lead_advance" />
			</Text>
		</Text>
		<Text as="p" variant="body" size="small" className="text-typography-weak">
			<Translate value="corgi.ambassadors.earnings.lead_historical" />
		</Text>
	</div>
);

type EarningsEstimateProps = Readonly<{
	earningsEstimate: number;
	isEarningsEstimateRange: boolean;
	earningsRangeMin: number;
	earningsRangeMax: number;
}>;

/**
 * Renders the earnings estimate amount as either a single currency value or a min–max range,
 * localized inside ClientRender for the same reason as the slider readout: Intl output must not
 * differ between the server render and hydration.
 */
const EarningsPanelAmount: React.FC<EarningsEstimateProps> = ({
	earningsEstimate,
	isEarningsEstimateRange,
	earningsRangeMin,
	earningsRangeMax,
}) => {
	const { localizeCurrency } = useI18n();

	return (
		<Text
			as="p"
			variant="heading"
			size={isEarningsEstimateRange ? 'large' : 'x-large'}
			// min-h-14 is the x-large branch's line box, so the panel keeps one height across both sizes.
			className="text-fill-interactive mb-1 flex min-h-14 items-center"
		>
			{isEarningsEstimateRange ? (
				<>
					<ClientRender placeholder={String(earningsRangeMin)}>
						{() => localizeCurrency(earningsRangeMin, EARNINGS_ESTIMATE_CURRENCY, EARNINGS_ESTIMATE_CURRENCY_OPTIONS)}
					</ClientRender>
					–
					<ClientRender placeholder={String(earningsRangeMax)}>
						{() => localizeCurrency(earningsRangeMax, EARNINGS_ESTIMATE_CURRENCY, EARNINGS_ESTIMATE_CURRENCY_OPTIONS)}
					</ClientRender>
				</>
			) : (
				<>
					~
					<ClientRender placeholder={String(earningsEstimate)}>
						{() => localizeCurrency(earningsEstimate, EARNINGS_ESTIMATE_CURRENCY, EARNINGS_ESTIMATE_CURRENCY_OPTIONS)}
					</ClientRender>
				</>
			)}
		</Text>
	);
};

const EarningsPanel: React.FC<EarningsEstimateProps> = ({
	earningsEstimate,
	isEarningsEstimateRange,
	earningsRangeMin,
	earningsRangeMax,
}) => {
	const { translate } = useI18n();

	return (
		<ThemeProvider mode="dark">
			<aside aria-live="polite" className="bg-background-raised border-stroke-weaker rounded-large border p-7">
				<Text
					variant="body"
					size="small"
					fontWeight="bold"
					className="text-typography-weak mb-4 block tracking-wide uppercase"
				>
					{translate('corgi.ambassadors.earnings.panel_label')}
				</Text>
				<EarningsPanelAmount
					earningsEstimate={earningsEstimate}
					isEarningsEstimateRange={isEarningsEstimateRange}
					earningsRangeMin={earningsRangeMin}
					earningsRangeMax={earningsRangeMax}
				/>
				<Text as="p" variant="body" size="medium" className="text-typography-weak mb-4">
					{translate('corgi.ambassadors.earnings.panel_basis')}
				</Text>
				<Separator orientation="horizontal" className="mb-4" />
				<Text as="p" variant="body" size="small" className="text-typography-weak">
					{translate('corgi.ambassadors.earnings.panel_note')}
				</Text>
			</aside>
		</ThemeProvider>
	);
};

export const EarningsCalculator: React.FC = () => {
	const { translate, localizeNumber } = useI18n();
	const sliderInstructionsId = useId();
	const {
		data: {
			signatureEstimate,
			minSignatureEstimate,
			maxSignatureEstimate,
			signatureEstimateStep,
			earningsEstimate,
			isEarningsEstimateRange,
			earningsRangeMin,
			earningsRangeMax,
		},
		actions: { onSliderChange, onSliderChangeEnd },
	} = useEarningsCalculator();

	return (
		<section id="earnings" className="py-12 md:py-16">
			<div className="container">
				<Text
					variant="body"
					size="small"
					fontWeight="bold"
					className="text-stroke-brand-strong mb-1.5 tracking-wide uppercase"
				>
					{translate('corgi.ambassadors.earnings.label')}
				</Text>
				<Text as="h2" variant="heading" size="large" className="text-typography-strong mb-2">
					{translate('corgi.ambassadors.earnings.heading')}
				</Text>
				<EarningsLeadIn />
				<div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[1fr_20rem]">
					<div>
						<Text as="p" variant="body" size="small" className="text-typography-weak mb-5">
							{translate('corgi.ambassadors.earnings.slider_question')}
						</Text>
						<Text as="p" variant="display" size="medium" className="text-typography-strong mb-5">
							<ClientRender placeholder={String(signatureEstimate)}>
								{() => localizeNumber(signatureEstimate)}
							</ClientRender>{' '}
							<Text as="span" variant="body" size="medium" className="text-typography-weak">
								{translate('corgi.ambassadors.earnings.readout_label')}
							</Text>
						</Text>
						{/* Deliberately no `formatValue`. It renders fine server-side for the visible
						    surfaces (tooltip, range labels), but react-aria does NOT apply it to
						    `aria-valuetext` during SSR — there it falls back to its own NumberFormatter, and
						    only picks up `formatValue` once hydrated. With no tooltip and no range labels
						    here, `aria-valuetext` was its only consumer, so passing one meant the two sides
						    formatted that attribute through different code: harmless for en-US, a hydration
						    mismatch in any locale where they disagree. Without it both sides use react-aria's
						    formatter and still announce a localized value ("25,000"), verified against the
						    server HTML. If a tooltip or `layout` with labels is ever enabled, prefer
						    `formatOptions` (declarative, applied consistently on both sides) over
						    `formatValue`. */}
						{/* The slider ships no range/keyboard guidance of its own — despite what the prop
						    docs claim, there is no English fallback in the built component, so without this
						    there is no aria-describedby at all. Supplied as our own visually-hidden node and
						    wired up by id rather than via `accessibilityInstructions`, because that prop makes
						    react-aria emit an invalid empty `aria-details`, which trips axe's label-title-only.
						    Bounds are interpolated from the same constants that drive the slider, so they
						    cannot drift the way slider_min_label / slider_max_label can, and are left
						    unformatted to keep Intl output out of a server-rendered text node. */}
						<span id={sliderInstructionsId} className="sr-only">
							{translate('corgi.ambassadors.earnings.slider_instructions', {
								min: String(minSignatureEstimate),
								max: String(maxSignatureEstimate),
							})}
						</span>
						<Slider
							ariaLabel={translate('corgi.ambassadors.earnings.slider_question')}
							aria-describedby={sliderInstructionsId}
							value={signatureEstimate}
							onChange={onSliderChange}
							onChangeEnd={onSliderChangeEnd}
							minValue={minSignatureEstimate}
							maxValue={maxSignatureEstimate}
							step={signatureEstimateStep}
							name="signatureEstimate"
						/>
						<SliderRangeLabels min={minSignatureEstimate} max={maxSignatureEstimate} />
					</div>
					<EarningsPanel
						earningsEstimate={earningsEstimate}
						isEarningsEstimateRange={isEarningsEstimateRange}
						earningsRangeMin={earningsRangeMin}
						earningsRangeMax={earningsRangeMax}
					/>
				</div>
			</div>
		</section>
	);
};
